import { makeAutoObservable } from "mobx"
import { subYears } from "date-fns"

import StudentAssignmentFolderItem from "../../../app/controllers/student-assignments-list/student-assignment-folder-item"
import StudentCacheService from "../../../app/services/student-cache/student-cache.service"
import StorageService from "../../../app/services/storage/storage.service"
import NotificationService from "../../../app/services/notification/notification.service"
import AssignmentWorkService from "../../../app/services/assignment-work/assignment-work.service"
import FeedbackService from "../../../app/services/feedback/feedback.service"
import AuthService from "../../../app/services/auth/auth.service"
import type { IStudentAssignmentsStore } from "./IStudentAssignmentsStore"

export interface Folder {
  id: string
  title: string
  color: string
  activeFolder: StudentAssignmentFolderItem
  inactiveFolder: StudentAssignmentFolderItem
}

interface RostersAndOwners {
  rosters: Map<string, Roster>
  rosterOwners: Map<string, User>
}

interface AssignmentsAndWorks {
  assignments: Map<string, Assignment>
  works: Map<string, AssignmentWork>
  assignmentRosters: Map<string, AssignmentRoster[]>
}

interface Roster {
  id: string
  name: string
  color: string
  ownerId: string
}

interface User {
  id: string
  name: string
}

interface Assignment {
  id: string
  rosters: string[]
}

interface AssignmentWork {
  id: string
  rosterId: string
  modified: Date
  assignment: Assignment & { id: string }
  assignmentRoster?: { hideStudentWork: boolean }
}

interface AssignmentRoster {
  hideStudentWork: boolean
}

// ideally we would have a store per domain object (rosters, assignments,
// assignment works, etc), but the data from the API is bundled together.

export class StudentAssignmentsStore implements IStudentAssignmentsStore {
  folders: Folder[] = []

  constructor(
    private studentCacheService: StudentCacheService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private assignmentWorkService: AssignmentWorkService,
    private feedbackService: FeedbackService,
    private authService: AuthService,
  ) {
    makeAutoObservable(this)
    this.subscribeToUpdates()
  }

  private subscribeToUpdates(): void {
    this.studentCacheService.userRosterUpdated.subscribe(
      this.handleUserRosterUpdated,
      this,
    )
  }

  destroy(): void {
    this.studentCacheService.userRosterUpdated.unsubscribe(
      this.handleUserRosterUpdated,
      this,
    )
  }

  private async handleUserRosterUpdated(): Promise<void> {
    await this.getFolders()
  }

  async getFolders(): Promise<Folder[]> {
    const [rostersAndOwners, assignmentsAndWork] = await Promise.all([
      this.studentCacheService.getUserRostersAndOwners(),
      this.studentCacheService.getUserAssignmentsAndWorks(),
    ])

    const folders = this.buildFoldersFromData(
      rostersAndOwners,
      assignmentsAndWork,
    )

    if (this.authService.isSuperuser) {
      this.addOrphanedFolder(folders, rostersAndOwners, assignmentsAndWork)
    }

    this.folders = folders
    return this.folders
  }

  private buildFoldersFromData(
    rostersAndOwners: RostersAndOwners,
    assignmentsAndWork: AssignmentsAndWorks,
  ): Folder[] {
    const folderStatus = this.storageService.studentFolderStatus || {}

    const folders = [...rostersAndOwners.rosters.values()].map((roster) => {
      const { activeFolder, inactiveFolder } =
        this.setUpActiveAndInactiveFolders(
          folderStatus,
          assignmentsAndWork,
          roster,
          rostersAndOwners,
        )

      const rosterOwner = rostersAndOwners.rosterOwners.get(roster.ownerId)
      const rosterOwnerName = rosterOwner ? `${rosterOwner.name} - ` : ""

      return {
        id: roster.id,
        title: `${rosterOwnerName}${roster.name}`,
        color: roster.color,
        activeFolder,
        inactiveFolder,
      }
    })

    folders.sort((a, b) => a.title.localeCompare(b.title))

    return folders
  }

  private setUpActiveAndInactiveFolders(
    folderStatus: Record<string, boolean>,
    assignmentsAndWork: AssignmentsAndWorks,
    roster: Roster,
    rostersAndOwners: RostersAndOwners,
  ): {
    activeFolder: StudentAssignmentFolderItem
    inactiveFolder: StudentAssignmentFolderItem
  } {
    const works = this.filterStudentWorks(
      assignmentsAndWork.works,
      assignmentsAndWork.assignments,
      roster,
    )
    const rosterOwner = rostersAndOwners.rosterOwners.get(roster.ownerId)

    const activeData = {
      roster,
      rosterOwner,
      unstartedAssignments: works.unstartedAssignments,
      works: works.activeWork,
      assignmentRosters: assignmentsAndWork.assignmentRosters,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onAssignmentRosterUpdated: () => {},
      open: folderStatus[roster.id] ?? false,
      notificationService: this.notificationService,
      assignmentWorkService: this.assignmentWorkService,
      feedbackService: this.feedbackService,
      activeWork: true,
    }

    const inactiveData = {
      roster,
      rosterOwner,
      unstartedAssignments: works.unstartedAssignments,
      works: works.inactiveWork,
      assignmentRosters: assignmentsAndWork.assignmentRosters,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onAssignmentRosterUpdated: () => {},
      open: folderStatus[`${roster.id}-inactive`] ?? false,
      notificationService: this.notificationService,
      assignmentWorkService: this.assignmentWorkService,
      feedbackService: this.feedbackService,
      activeWork: false,
    }

    return {
      activeFolder: StudentAssignmentFolderItem.fromData(activeData),
      inactiveFolder: StudentAssignmentFolderItem.fromData(inactiveData),
    }
  }

  private filterStudentWorks(
    works: Map<string, AssignmentWork>,
    assignments: Map<string, Assignment>,
    roster: Roster,
  ): {
    activeWork: AssignmentWork[]
    inactiveWork: AssignmentWork[]
    unstartedAssignments: Assignment[]
  } {
    const activeWork: Record<string, AssignmentWork> = {}
    const inactiveWork: Record<string, AssignmentWork> = {}
    const oneYearAgo = subYears(new Date(), 1)

    Array.from(works.values()).forEach((w) => {
      if (
        w.rosterId === roster.id &&
        w.assignment.rosters.includes(w.rosterId)
      ) {
        if (w.modified >= oneYearAgo) {
          activeWork[w.assignment.id] = w
        } else {
          inactiveWork[w.assignment.id] = w
        }
      }
    })

    const unstartedAssignments = Array.from(assignments.values()).filter(
      (a) =>
        !inactiveWork[a.id] &&
        !activeWork[a.id] &&
        a.rosters.includes(roster.id),
    )

    return {
      activeWork: Object.values(activeWork),
      inactiveWork: Object.values(inactiveWork),
      unstartedAssignments,
    }
  }

  private addOrphanedFolder(
    folders: Folder[],
    rostersAndOwners: RostersAndOwners,
    assignmentsAndWork: AssignmentsAndWorks,
  ): void {
    const folderStatus = this.storageService.studentFolderStatus || {}
    const orphans = [...assignmentsAndWork.works.values()].filter((work) => {
      const studentIsOnRoster = rostersAndOwners.rosters.has(work.rosterId)
      const rosterIsAssignedToAssignment = work.assignment.rosters.includes(
        work.rosterId,
      )
      const assignmentIsHidden = work.assignmentRoster?.hideStudentWork
      return (
        (!studentIsOnRoster || !rosterIsAssignedToAssignment) &&
        !assignmentIsHidden
      )
    })

    if (orphans.length === 0) {
      return
    }

    const activeFolder = StudentAssignmentFolderItem.orphaned(
      orphans,
      assignmentsAndWork.assignmentRosters,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      folderStatus,
      this.notificationService,
      this.assignmentWorkService,
      this.feedbackService,
    )

    const inactiveFolder = StudentAssignmentFolderItem.orphaned(
      [],
      assignmentsAndWork.assignmentRosters,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      folderStatus,
      this.notificationService,
      this.assignmentWorkService,
      this.feedbackService,
    )

    folders.push({
      id: "orphaned",
      title: "Old Work",
      color: "#404041",
      activeFolder,
      inactiveFolder,
    })
  }
}
